import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    GridApi,
    ColDef,
    TextEditorModule,
    LargeTextEditorModule,
    CellEditingStartedEvent,
    CellEditingStoppedEvent,
    CellClickedEvent,
    NumberEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    NewValueParams,
    ScrollApiModule,
    HighlightChangesModule,
    TooltipModule,
} from "ag-grid-community";

import {
    NgbModal,
    NgbModalOptions,
} from "@ng-bootstrap/ng-bootstrap";

import { CreateContractDetailsModalComponent } from "./components/create-contract-details-modal/create-contract-details-modal.component";
import { ContractDetailsApiService } from "src/app/core/api/contract-details-api.service";
import { ContractApiService } from "src/app/core/api/contract-api.service";
import { ExtensionApiService } from "src/app/core/api/extension-api.service";
import { ContractDetails } from "src/app/core/models/contract-details";
import { formatDate } from "@angular/common";
import { ToastrService } from "ngx-toastr";
import { ActionsComponent } from "./components/actions/actions.component";
import { CommentCellRendererComponent, stripHtml } from "../contract-profile/components/comment-cell-renderer/comment-cell-renderer.component";
import { CommentEditModalComponent } from "../contract-profile/components/comment-edit-modal/comment-edit-modal.component";
import { take } from "rxjs";
import { SpinnerService } from "src/app/core/services/spinner.service";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    TextEditorModule,
    LargeTextEditorModule,
    NumberEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    ScrollApiModule,
    HighlightChangesModule,
    TooltipModule,
]);

@Component({
    selector: "app-contract-details",
    standalone: true,
    imports: [AgGridAngular, TranslatePipe, FormsModule, ReactiveFormsModule],
    templateUrl: "./contract-details.component.html",
    styleUrl: "./contract-details.component.scss",
})
export class ContractDetailsComponent implements OnInit {
    public isCellEditing = false;
    getRowStyle: any;
    gridApi: GridApi<ContractDetails>;

    public contractId: number;
    public ownerType: "contract" | "extension";
    public ownerId: number;

    public taskTitle = "";
    public ownerLabel = "";

    public contractDetailsList: ContractDetails[];
    public gridTitles: any = {};
    public colDefs: ColDef<ContractDetails>[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private modalService: NgbModal,
        private contractDetailsApiService: ContractDetailsApiService,
        private contractApiService: ContractApiService,
        private extensionApiService: ExtensionApiService,
        private translateService: TranslateService,
        private toastr: ToastrService,
        private spinner: SpinnerService
    ) {}

    public validationOnEmptyCell(event: NewValueParams<ContractDetails, any, any>) {
        event.data[event.colDef.field as any] = event.oldValue;
        this.gridApi.refreshCells({ force: true });

        this.gridApi.startEditingCell({
            rowIndex: event.node.rowIndex,
            colKey: event.colDef.field,
        });
    }

    public updateField(eventData: NewValueParams<ContractDetails, any, any>) {
        this.contractDetailsApiService
            .update(eventData.data)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.translateService
                        .get("contractDetails.form.update", {
                            field: eventData.colDef.headerName,
                        })
                        .subscribe((res) => this.toastr.success(res));
                },
                error: () => {
                    this.translateService
                        .get("contractDetails.form.updateError", {
                            field: eventData.colDef.headerName,
                        })
                        .subscribe((res) => this.toastr.error(res));
                },
            });
    }

    private openCommentEditor(event: CellClickedEvent<ContractDetails>): void {
        const options: NgbModalOptions = { centered: true, backdrop: true, size: 'lg' };
        const modalRef = this.modalService.open(CommentEditModalComponent, options);
        modalRef.componentInstance.initialValue = event.value ?? '';
        modalRef.result.then((newValue: string) => {
            if (newValue === event.value) return;
            const updated: ContractDetails = { ...event.data, comment: newValue };
            event.node.setDataValue('comment', newValue);
            this.contractDetailsApiService.update(updated).pipe(take(1)).subscribe({
                next: () => this.translateService.get('contractDetails.form.update', { field: event.colDef.headerName })
                    .subscribe(res => this.toastr.success(res)),
                error: () => {
                    event.node.setDataValue('comment', event.value);
                    this.translateService.get('contractDetails.form.updateError', { field: event.colDef.headerName })
                        .subscribe(res => this.toastr.error(res));
                },
            });
        }).catch(() => {});
    }

    defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };

    autoGroupColumnDef: ColDef = {
        minWidth: 200,
    };

    gridContext = {
        parentComponent: this,
    };

    private dateFilterGetter = (field: "startDate" | "endDate" | "createdDate") =>
        (params: any) =>
            params.data?.[field]
                ? formatDate(params.data[field], "dd-MM-yyyy", "en")
                : "";

    private initializeColumnDefs() {
        this.colDefs = [
            {
                field: "createdDate",
                headerName: this.gridTitles.createdDate,
                valueFormatter: (params) =>
                    params.value ? formatDate(params.value, "dd-MM-yyyy", "en") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: this.dateFilterGetter("createdDate"),
                width: 130,
                sort: "desc",
            },
            {
                field: "startDate",
                headerName: this.gridTitles.startDate,
                valueFormatter: (params) =>
                    params.value ? formatDate(params.value, "dd-MM-yyyy", "en") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: this.dateFilterGetter("startDate"),
                width: 130,
            },
            {
                field: "endDate",
                headerName: this.gridTitles.endDate,
                valueFormatter: (params) =>
                    params.value ? formatDate(params.value, "dd-MM-yyyy", "en") : "",
                filter: "agTextColumnFilter",
                filterValueGetter: this.dateFilterGetter("endDate"),
                width: 130,
            },
            {
                field: "po",
                headerName: this.gridTitles.poNumber,
                headerTooltip: this.gridTitles.poNumberTooltip,
                filter: "agTextColumnFilter",
                editable: true,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                field: "caseNumber",
                headerName: this.gridTitles.caseNumber,
                editable: true,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                field: "budget",
                headerName: this.gridTitles.budget,
                editable: true,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                field: "comment",
                headerName: this.gridTitles.comment,
                filter: "agTextColumnFilter",
                flex: 1,
                minWidth: 100,
                editable: false,
                resizable: false,
                cellRenderer: CommentCellRendererComponent,
                filterValueGetter: (params) => stripHtml(params.data?.comment),
                onCellClicked: (event: CellClickedEvent<ContractDetails>) => this.openCommentEditor(event),
            },
            {
                field: "itemNumbersCount",
                headerName: this.gridTitles.itemNumbers,
                filter: false,
                width: 130,
                cellClass: "link-cell",
                cellRenderer: (params: any) =>
                    `<span class="link-cell-content">${params.value ?? 0} <i class="fa fa-chevron-right link-arrow"></i></span>`,
                onCellClicked: (event: CellClickedEvent<ContractDetails>) => {
                    if (event.data) {
                        this.goToItemNumbers(event.data.id);
                    }
                },
            },
            {
                headerName: this.gridTitles.actions ?? "Actions",
                cellRenderer: ActionsComponent,
                cellRendererParams: {
                    context: this.gridContext,
                },
                filter: false,
                width: 120,
                pinned: "right",
                resizable: false,
                lockPinned: true,
                lockPosition: true,
            },
        ];
    }

    ngOnInit(): void {
        this.contractId = Number(this.route.snapshot.paramMap.get("contractId"));
        this.ownerType = this.route.snapshot.paramMap.get("ownerType") as "contract" | "extension";
        this.ownerId = Number(this.route.snapshot.paramMap.get("ownerId"));

        this.translateService.stream("contractDetails.form").subscribe((res) => {
            this.gridTitles = res;
            this.initializeColumnDefs();
        });
        this.loadContractDetailsList();
        this.loadHeaderInfo();
    }

    private loadHeaderInfo() {
        this.contractApiService
            .getContractByContractId(this.contractId)
            .subscribe((contract) => {
                this.taskTitle = contract?.taskSmall?.title ?? "";
            });

        if (this.ownerType === "extension") {
            this.extensionApiService
                .getByContract(this.contractId)
                .subscribe((extensions) => {
                    const index = extensions.findIndex(
                        (e) => e.id === this.ownerId
                    );
                    const number = index >= 0 ? index + 1 : this.ownerId;
                    this.ownerLabel = this.translateService.instant(
                        "extensions.extensionLabel",
                        { number }
                    );
                });
        } else {
            this.ownerLabel = this.translateService.instant(
                "extensions.contractLabel",
                { number: 1 }
            );
        }
    }

    public goToExtensions() {
        this.router.navigate(["contract", this.contractId, "details"]);
    }

    private loadContractDetailsList() {
        const owner = this.ownerType === "extension"
            ? { extensionId: this.ownerId }
            : { contractId: this.ownerId };

        this.contractDetailsApiService.getByOwner(owner).subscribe({
            next: (result) => {
                this.contractDetailsList = result;
                if (this.gridApi) {
                    this.gridApi.setGridOption("rowData", this.contractDetailsList);
                }
            }
        });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        this.gridApi.setGridOption("rowData", this.contractDetailsList);
    }

    onCellEditingStarted(event: CellEditingStartedEvent) {
        this.isCellEditing = true;
        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "#b0d7efc9" };
            }
            return null;
        };
    }

    onCellEditingStopped(event: CellEditingStoppedEvent) {
        this.isCellEditing = false;
        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "" };
            }
            return null;
        };
    }

    public goToItemNumbers(contractDetailsId: number) {
        this.router.navigate([
            "contract",
            this.contractId,
            "details",
            this.ownerType,
            this.ownerId,
            "contract-details",
            contractDetailsId,
            "items",
        ]);
    }

    public goBack() {
        this.router.navigate(["contract", this.contractId, "details"]);
    }

    public openModal() {
        const options: NgbModalOptions = {
            centered: true,
            backdrop: true,
            size: "lg",
        };

        const modalRef = this.modalService.open(CreateContractDetailsModalComponent, options);
        if (this.ownerType === "extension") {
            modalRef.componentInstance.extensionId = Number(this.ownerId);
        } else {
            modalRef.componentInstance.contractId = Number(this.ownerId);
        }

        modalRef.result.then(
            (result) => {
                this.gridApi.applyTransaction({ add: [result] });
            },
            () => {}
        );
    }
}
